if (!customElements.get('vintage-bestsellers-slider')) {
  customElements.define(
    'vintage-bestsellers-slider',
    class VintageBestsellersSlider extends HTMLElement {
      connectedCallback() {
        if (this._vbReady) return;
        this._vbReady = true;

        this.viewport = this.querySelector('.vintage-bestsellers__viewport');
        this.track = this.querySelector('[data-vbest-track]');
        this.items = Array.from(this.querySelectorAll('[data-vbest-item]'));
        this.prev = this.querySelector('[data-vbest-prev]');
        this.next = this.querySelector('[data-vbest-next]');
        this.dotsWrap = this.querySelector('[data-vbest-dots]');
        this.index = 0;

        if (!this.viewport || !this.track || !this.items.length) return;

        this.onPrevClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.prev?.hasAttribute('disabled')) return;
          this.go(this.index - 1, true);
        };
        this.onNextClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.next?.hasAttribute('disabled')) return;
          this.go(this.index + 1, true);
        };

        this.prev?.addEventListener('click', this.onPrevClick);
        this.next?.addEventListener('click', this.onNextClick);

        this.onResize = () => this.scheduleRefresh();
        this.onLoad = () => this.refresh();
        window.addEventListener('resize', this.onResize);
        window.addEventListener('load', this.onLoad);

        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.refresh()).catch(() => {});
        }

        if ('ResizeObserver' in window) {
          let lastWidth = 0;
          this.resizeObserver = new ResizeObserver(() => {
            const w = this.viewport.clientWidth;
            if (Math.abs(w - lastWidth) < 2) return;
            lastWidth = w;
            this.scheduleRefresh();
          });
          this.resizeObserver.observe(this.viewport);
        }

        this.onScroll = () => this.syncIndexFromScroll();
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

        this.bindSwipe();
        this.refresh();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.refresh());
        });
      }

      disconnectedCallback() {
        this._vbReady = false;
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('load', this.onLoad);
        this.prev?.removeEventListener('click', this.onPrevClick);
        this.next?.removeEventListener('click', this.onNextClick);
        this.viewport?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      scheduleRefresh() {
        if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = null;
          this.refresh();
        });
      }

      isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      useScrollNav() {
        return this.isMobile();
      }

      perPage() {
        return this.isMobile() ? 2 : 4;
      }

      pageCount() {
        return Math.max(1, Math.ceil(this.items.length / this.perPage()));
      }

      shouldShowCarousel() {
        if (this.isMobile()) return this.items.length > 2;
        return this.items.length > this.perPage();
      }

      layoutSlides() {
        const gap = this.isMobile() ? 10 : 16;
        const per = this.perPage();
        const viewportWidth = this.viewport.clientWidth;
        if (!viewportWidth) return;
        const slideWidth = Math.floor((viewportWidth - gap * (per - 1)) / per);
        this.items.forEach((el) => {
          el.style.flex = `0 0 ${slideWidth}px`;
          el.style.width = `${slideWidth}px`;
          el.style.maxWidth = `${slideWidth}px`;
        });
      }

      refresh() {
        this.layoutSlides();
        this.renderDots();
        this.toggleControls();
        this.go(Math.min(this.index, this.pageCount() - 1), false);
      }

      renderDots() {
        if (!this.dotsWrap) return;
        const count = this.pageCount();
        this.dotsWrap.innerHTML = '';
        if (!this.shouldShowCarousel() || count < 2) return;
        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'vintage-bestsellers__dot' + (i === this.index ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Go to product group ' + (i + 1));
          dot.addEventListener('click', () => this.go(i, true));
          this.dotsWrap.appendChild(dot);
        }
      }

      toggleControls() {
        const showCarousel = this.shouldShowCarousel();
        this.classList.toggle('is-carousel', showCarousel);
        this.classList.toggle('is-mobile-nav', this.isMobile() && showCarousel);
        this.classList.toggle('is-scroll-nav', showCarousel && this.useScrollNav());
      }

      bindSwipe() {
        let startX = 0;
        let dragging = false;
        this.viewport.addEventListener(
          'touchstart',
          (event) => {
            startX = event.changedTouches[0].clientX;
            dragging = true;
          },
          { passive: true }
        );
        this.viewport.addEventListener(
          'touchend',
          (event) => {
            if (!dragging) return;
            dragging = false;
            const delta = event.changedTouches[0].clientX - startX;
            if (Math.abs(delta) > 40) this.go(this.index + (delta < 0 ? 1 : -1), true);
          },
          { passive: true }
        );
      }

      offsetFor(page) {
        const target = page * this.perPage();
        if (target <= 0) return 0;
        const item = this.items[target];
        if (!item) return 0;
        return Math.round(item.offsetLeft);
      }

      go(next, animate) {
        if (!this.shouldShowCarousel()) {
          this.index = 0;
          this.track.style.transform = '';
          this.viewport.scrollLeft = 0;
          this.updateDots();
          this.updateNavState();
          return;
        }

        const max = this.pageCount() - 1;
        this.index = Math.max(0, Math.min(Number(next), max));
        const offset = this.offsetFor(this.index);

        if (this.useScrollNav()) {
          this.track.style.transform = '';
          this.viewport.scrollTo({
            left: offset,
            behavior: animate ? 'smooth' : 'auto',
          });
        } else {
          this.viewport.scrollLeft = 0;
          this.track.style.transform = `translate3d(-${offset}px,0,0)`;
        }

        this.updateDots();
        this.updateNavState();
      }

      syncIndexFromScroll() {
        if (!this.useScrollNav() || !this.shouldShowCarousel()) return;
        const scroll = this.viewport.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        for (let p = 0; p < this.pageCount(); p += 1) {
          const dist = Math.abs(this.offsetFor(p) - scroll);
          if (dist < bestDist) {
            bestDist = dist;
            best = p;
          }
        }
        if (best !== this.index) {
          this.index = best;
          this.updateDots();
          this.updateNavState();
        }
      }

      updateNavState() {
        if (!this.shouldShowCarousel()) {
          this.prev?.removeAttribute('disabled');
          this.next?.removeAttribute('disabled');
          return;
        }
        const max = this.pageCount() - 1;
        this.prev?.toggleAttribute('disabled', this.index <= 0);
        this.next?.toggleAttribute('disabled', this.index >= max);
      }

      updateDots() {
        this.dotsWrap?.querySelectorAll('.vintage-bestsellers__dot').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.index);
        });
      }
    }
  );
}
